/**
 * readinessQuestions.js — Question catalog + Owen copy for the borrower
 * Readiness experience (`/readiness`).
 *
 * This is the simplified 12–13 question assessment from the redesign handoff.
 * It intentionally mirrors the category structure of
 * `src/utils/readinessScoring.js` (Income 25 / Assets 20 / Credit 20 /
 * Structure 15 / Docs 10 / Intent 10) and the metadata shape of
 * `src/config/recommendationQuestions.js` (id / type / options / showIf /
 * education), trimmed to what this flow needs.
 *
 * All copy here is final design intent — keep verbatim.
 */

// ── Questions ───────────────────────────────────────────────────
export const READINESS_QUESTIONS = [
  {
    id: 'purpose', type: 'chips',
    text: "First things first — are you buying a home, or refinancing one you already own?",
    options: [
      { v: 'purchase', l: 'Buying a home' },
      { v: 'refinance', l: 'Refinancing' },
    ],
    why: { title: 'Purpose drives everything', body: 'Buying and refinancing follow different program paths, so this one answer shapes every question after it.' },
  },
  {
    id: 'existingLoanType', type: 'chips',
    text: "What kind of loan do you have now?",
    showIf: (a) => a.purpose === 'refinance',
    options: [
      { v: 'conventional', l: 'Conventional' },
      { v: 'fha', l: 'FHA' },
      { v: 'va', l: 'VA' },
      { v: 'usda', l: 'USDA' },
      { v: 'other', l: "Not sure / other" },
    ],
    why: { title: 'Streamline shortcuts', body: 'FHA, VA, and USDA loans each have streamlined refinance paths with far less paperwork — but only from the same loan type. Knowing yours unlocks (or rules out) those shortcuts immediately.' },
  },
  {
    id: 'refiKind', type: 'chips',
    text: "What's the main goal of the refinance?",
    showIf: (a) => a.purpose === 'refinance',
    options: [
      { v: 'rate-term', l: 'Lower my rate or payment' },
      { v: 'cash-out', l: 'Take cash out' },
    ],
    why: { title: 'Two different paths', body: 'Rate/term and cash-out refinances follow different program rules, loan-to-value limits, and pricing — this one answer splits the product universe.' },
  },
  {
    id: 'propertyUse', type: 'chips',
    text: "How will you use the property?",
    options: [
      { v: 'primary', l: "It's my home" },
      { v: 'secondary', l: 'Second home' },
      { v: 'investment', l: 'Investment property' },
    ],
    why: { title: 'Occupancy sets your options', body: 'Primary residences get the best rates. Investment properties open programs that qualify on rental income instead.' },
  },
  {
    id: 'propertyType', type: 'chips',
    text: "What kind of property is it?",
    options: [
      { v: 'single-family', l: 'Single-family home' },
      { v: 'condo', l: 'Condo' },
      { v: 'townhouse', l: 'Townhouse' },
      { v: 'multi', l: '2–4 units' },
    ],
    why: { title: 'Property type matters', body: 'Some programs are limited to single-family homes; condos and multi-unit properties have their own paths.' },
  },
  {
    id: 'renovationNeeded', type: 'chips',
    text: "Are you planning to finance renovation work as part of this loan?",
    options: [
      { v: 'no', l: 'No' },
      { v: 'yes', l: 'Yes, include renovation costs' },
    ],
    why: { title: 'Renovation programs', body: "Financing renovations changes the program menu — options like HomeStyle, CHOICERenovation, and FHA 203(k) roll the work into the loan. If you don't need it, we can set those aside." },
  },
  {
    id: 'value', type: 'currency', ph: '450,000',
    text: "Roughly what's the price of the home?",
    textRefi: "Roughly what's your home worth today?",
    hint: 'A ballpark is fine — you can refine this anytime.',
    why: { title: 'Value sets your loan size', body: "Combined with your down payment, this tells us your loan amount and whether you're in conforming or jumbo territory." },
  },
  {
    id: 'down', type: 'currency', ph: '90,000',
    text: "How much are you planning to put down?",
    textRefi: "About how much is left on your current loan?",
    hint: "No pressure — plenty of great programs need as little as 0–3% down.",
    hintRefi: 'Your latest statement has the exact number, but an estimate works.',
    why: { title: 'Down payment → LTV', body: 'This sets your loan-to-value ratio, which affects your rate and whether mortgage insurance applies. 20%+ eliminates PMI.' },
    learn: { title: 'Down payment, LTV & PMI', body: "Your down payment (or equity, on a refinance) sets your loan-to-value — the loan as a percentage of the home's value. At or below 80% LTV, conventional loans skip mortgage insurance entirely; above it, PMI adds roughly 0.3–1.5% of the loan per year until you reach 20% equity. More down earns better rate tiers, but plenty of programs work with as little as 0–3.5% down." },
  },
  {
    id: 'credit', type: 'chips',
    text: "What's your best guess at your credit score? An estimate is totally fine.",
    options: [
      { v: '760+', l: '760 or higher', badge: 'Excellent' },
      { v: '700-759', l: '700–759', badge: 'Very good' },
      { v: '660-699', l: '660–699', badge: 'Good' },
      { v: '600-659', l: '600–659', badge: 'Fair' },
      { v: '<600', l: 'Below 600' },
      { v: 'unsure', l: "I'm not sure" },
    ],
    why: { title: 'The biggest rate factor', body: "Your credit score is the single biggest driver of your interest rate. We never pull your credit at this stage — your estimate is enough." },
    learn: { title: 'How credit drives your rate', body: "Lenders price in credit bands — 760+ gets the best tiers, and each band below adjusts the rate a little. Nothing here pulls your credit, and checking your own score never hurts it. If you're near the top of a band, even a 10–20 point improvement before you lock can step you into a better rate." },
  },
  {
    id: 'employment', type: 'chips',
    text: "How do you earn your income?",
    options: [
      { v: 'w2', l: 'Employed (W-2)' },
      { v: 'self', l: 'Self-employed' },
      { v: 'retired', l: 'Retired' },
      { v: 'other', l: 'Other' },
    ],
    why: { title: 'Documentation path', body: 'This decides how income gets verified. Self-employed? There are programs that use bank statements instead of tax returns.' },
  },
  {
    id: 'income', type: 'currency', ph: '110,000',
    text: "What's your total annual income, before taxes?",
    hint: 'Include everything — salary, bonuses, side income, benefits.',
    why: { title: 'Affordability check', body: 'Lenders want your total debt payments below roughly 43–50% of gross income. This helps us gauge your comfortable range.' },
    learn: { title: 'What counts as income', body: "Salary, hourly wages, bonuses, commission, side income, and benefits can all count — what lenders care about is that it's steady and documentable, with two years of history as the usual benchmark. Self-employed? There are programs that qualify you on bank-statement deposits instead of tax returns." },
  },
  {
    id: 'debts', type: 'currency', ph: '600',
    text: "And about how much do you pay toward debts each month? Car, cards, student loans — don't count rent.",
    hint: "Minimum payments only. If it's zero, enter 0.",
    why: { title: 'Debt-to-income ratio', body: 'Lower monthly debts mean more room for your housing payment. DTI is one of the top approval factors.' },
    learn: { title: 'Debt-to-income (DTI), explained', body: "Lenders divide your monthly debt payments — plus the new house payment — by your gross monthly income. Most programs want that total under 43–50%. Only minimum payments count, not what you actually pay each month, and your current rent stops counting once you own." },
  },
  {
    id: 'firstTime', type: 'chips',
    text: "Is this your first time buying a home?",
    showIf: (a) => a.purpose === 'purchase',
    options: [
      { v: 'yes', l: 'Yes, first time' },
      { v: 'no', l: "No, I've owned before" },
    ],
    why: { title: 'First-timer perks', body: 'First-time buyers unlock affordable agency programs and down-payment assistance in many states.' },
  },
  {
    id: 'military', type: 'chips',
    text: "Have you or your spouse served in the military?",
    options: [
      { v: 'yes', l: 'Yes' },
      { v: 'no', l: 'No' },
    ],
    why: { title: 'VA eligibility', body: 'Service members, veterans, and qualifying spouses can access VA loans — 0% down and no mortgage insurance.' },
    learn: { title: 'The VA benefit', body: "Service members, veterans, and some surviving spouses qualify for VA loans: 0% down, no monthly mortgage insurance, and competitive rates. A 10%+ disability rating waives the funding fee entirely. When you've earned it, it's usually the strongest program on the board." },
  },
  {
    id: 'state', type: 'chips',
    text: "Almost done — which state is the property in?",
    hint: "We're starting in California, with more states on the way.",
    options: [
      { v: 'ca', l: 'California' },
      { v: 'other', l: 'Another state' },
    ],
    why: { title: 'Location shapes eligibility', body: 'State and county determine loan limits, USDA rural eligibility, and local assistance programs.' },
  },
  {
    id: 'county', type: 'chips',
    text: "Last one — which county? This lets me check things like USDA rural eligibility for you.",
    showIf: (a) => a.state === 'ca',
    options: [
      { v: 'los-angeles', l: 'Los Angeles', rural: 'no' },
      { v: 'orange', l: 'Orange', rural: 'no' },
      { v: 'san-diego', l: 'San Diego', rural: 'no' },
      { v: 'sacramento', l: 'Sacramento', rural: 'unsure' },
      { v: 'placer', l: 'Placer', rural: 'unsure' },
      { v: 'fresno', l: 'Fresno', rural: 'yes' },
      { v: 'humboldt', l: 'Humboldt', rural: 'yes' },
      { v: 'other', l: 'Another county', rural: 'unsure' },
    ],
    why: { title: 'County-level checks', body: 'Counties set conforming loan limits, and many have USDA-eligible areas — more places qualify than most people expect.' },
  },

  // ── Term-strategy questions (drive which loan structure fits best) ──
  {
    id: 'primaryMortgageGoal', type: 'chips',
    text: "Now the fun part — what matters most to you about your mortgage?",
    options: [
      { v: 'lowest-payment', l: 'Lowest monthly payment' },
      { v: 'balanced', l: 'Balance payment & payoff' },
      { v: 'fast-payoff', l: 'Pay it off faster' },
      { v: 'build-equity', l: 'Build equity faster' },
      { v: 'lowest-interest', l: 'Lowest lifetime interest' },
      { v: 'flexibility', l: 'Maximum flexibility' },
    ],
    why: { title: 'Your goal picks the term', body: 'Longer terms mean lower required payments; shorter terms build equity and slash lifetime interest. Your priority — not a default — should drive which structure wins.' },
    learn: { title: 'Payment vs. payoff, explained', body: "A 30-year loan has the lowest required payment and the most flexibility — you can always pay extra. A 15-year roughly halves your lifetime interest but locks in a much higher required payment. 20- and 25-year terms sit in between. There's no universally right answer; there's the right answer for your cash flow and plans." },
  },
  {
    id: 'expectedYearsInHome', type: 'chips',
    text: "How long do you expect to keep this home — or at least this loan?",
    options: [
      { v: '<3', l: 'Under 3 years' },
      { v: '3-5', l: '3–5 years' },
      { v: '6-7', l: '6–7 years' },
      { v: '8-10', l: '8–10 years' },
      { v: '10+', l: '10+ years' },
      { v: 'unsure', l: 'Not sure' },
    ],
    why: { title: 'Time horizon matters', body: "If you'll move or refinance within a few years, some loan structures (like ARMs with a fixed intro period) may be worth comparing. Staying long-term usually favors fixed rates." },
  },
  {
    id: 'maxComfortablePayment', type: 'currency', ph: '3,500',
    text: "What monthly housing payment would feel genuinely comfortable? A ballpark is perfect.",
    hint: "Think total: loan payment, taxes, insurance. We'll never push past comfortable.",
    why: { title: 'The comfort test', body: 'A shorter term can save huge interest — but only if the higher required payment fits your life. This number keeps every recommendation honest.' },
  },
  {
    id: 'paymentStabilityPreference', type: 'chips',
    text: "How important is a payment that can never change because of interest rates?",
    options: [
      { v: 'essential', l: 'Essential — lock it in' },
      { v: 'important', l: 'Important' },
      { v: 'flexible', l: 'Some flexibility for savings' },
      { v: 'comfortable', l: "I'm fine with rate changes" },
    ],
    why: { title: 'Fixed vs adjustable', body: 'Fixed rates never move. Adjustable rates (ARMs) start fixed for 3–10 years, then can rise or fall with the market. Your comfort with that decides whether ARMs are even worth pricing.' },
  },
  {
    id: 'rateRiskTolerance', type: 'chips',
    text: "Would you accept the possibility of a future rate change if it meaningfully lowered your payment today?",
    showIf: (a) => a.paymentStabilityPreference !== undefined && a.paymentStabilityPreference !== 'essential'
      && a.expectedYearsInHome !== undefined && a.expectedYearsInHome !== '10+',
    options: [
      { v: 'no', l: 'No — keep it fixed' },
      { v: 'maybe', l: 'Maybe, if the savings are real' },
      { v: 'yes', l: 'Yes' },
    ],
    why: { title: 'The ARM trade-off', body: "An ARM only makes sense when live pricing shows real savings during the fixed period AND you're comfortable with what happens after. We never assume ARM rates are lower — the market decides that." },
  },
  {
    id: 'plannedExtraPayments', type: 'chips',
    text: "Do you expect to put extra money toward the loan principal?",
    showIf: (a) => ['fast-payoff', 'build-equity', 'lowest-interest'].includes(a.primaryMortgageGoal),
    options: [
      { v: 'no', l: 'Probably not' },
      { v: 'occasionally', l: 'Occasionally' },
      { v: 'monthly', l: 'Every month' },
      { v: 'large-periodic', l: 'Big chunks now and then' },
    ],
    why: { title: 'The flexible path to fast payoff', body: 'A 30-year loan plus voluntary extra principal can reach payoff nearly as fast as a shorter term — while keeping the required payment low if life changes. Worth weighing against a committed 15- or 20-year.' },
  },
];

/** Questions visible for a given answer set (branching via showIf). */
export function visibleQuestions(answers = {}) {
  return READINESS_QUESTIONS.filter((q) => !q.showIf || q.showIf(answers));
}

/** Parse a possibly comma-formatted currency string to a number. */
export function numFrom(v) {
  return parseFloat(String(v).replace(/[^0-9.]/g, '')) || 0;
}

/** USDA rural eligibility from the county answer: 'yes' | 'no' | 'unsure'. */
export function ruralAnswer(answers = {}) {
  if (answers.county === undefined) return 'unsure';
  const countyQ = READINESS_QUESTIONS.find((q) => q.id === 'county');
  const opt = countyQ.options.find((o) => o.v === answers.county);
  return opt ? opt.rural : 'unsure';
}

// ── Category meta (mirrors readinessScoring.js CATEGORIES) ─────
export const READINESS_CATEGORIES = [
  { id: 'income', label: 'Income readiness', max: 25, color: '#4f46e5' },
  { id: 'assets', label: 'Assets & cash', max: 20, color: '#059669' },
  { id: 'credit', label: 'Credit readiness', max: 20, color: '#7c3aed' },
  { id: 'structure', label: 'Loan structure fit', max: 15, color: '#d97706' },
  { id: 'docs', label: 'Documentation', max: 10, color: '#0ea5e9' },
  { id: 'intent', label: 'Timing & intent', max: 10, color: '#e11d48' },
];

// ── Owen's scripted copy ────────────────────────────────────────
export const OWEN_INTRO_MESSAGES = [
  "Hi, I'm Owen 👋 I'm here to help you figure out how mortgage-ready you are — no credit pull, no sales calls, nothing shared with anyone.",
  "One thing worth saying up front: I'm an AI. I don't view, judge, or hold onto your personal information the way a person would — your answers stay yours, and nothing goes to a lender until you choose to send it.",
  "I'll ask a short series of quick questions — only the ones that matter for your situation. Each answer narrows a field of 350+ loan products in real time, until we land on the one that fits you best.",
  { text: "One quick tip before we start — this is what I look like when I have more to share about a topic:", demo: 'learn' },
  "Takes about 3 minutes. Ready?",
];

export const OWEN_ANALYZING_MESSAGES = [
  "That's everything I need. Give me a second while I run your full profile against our catalog of 350+ loan products…",
  "Weighing eligibility, loan structure, and your goals across conventional, FHA, VA, USDA, jumbo, and specialty options — down to the specific term that fits you best.",
];

export const OWEN_SIDE_FOLLOWUP =
  "Does that help? Ask me anything else — I'm happy to explain as much as you want. And remember, I'm an AI: I don't view or keep any of your personal details.";

export const OWEN_RESUME_CHIP_LABEL = "I'm all set — let's continue";

export const OWEN_SKIP_RESPONSE =
  "No problem at all — you can add that anytime. No pressure here.";

export const OWEN_RESTART_MESSAGE =
  "Fresh start! Are you buying a home, or refinancing?";

export const ASK_LANE_PLACEHOLDER = 'Or ask Owen anything — \u201cWhat\u2019s PMI?\u201d';

export const SIDE_PENDING_HINT = 'Or type another question below.';

export function owenAppIntroMessages(program, email) {
  const prog = program || 'your program';
  return [
    "Great — we'll build your application around the " + prog + ". From here on, every question relates directly to that program. I will never ask for anything it doesn't need.",
    "Your progress is saved under " + email + ". No password — we'll email you a secure code whenever you come back. And to be clear: we never market, sell, or send anything you didn't ask for.",
    "Sections come a few fields at a time. Everything is optional — share what you're comfortable with, skip anything, and I'll tell you why each part matters.",
  ];
}

export function owenAppWrapupMessages(program, email) {
  const prog = program || 'your program';
  return [
    "That's the core of a full " + prog + " application — genuinely well done. Everything is saved to " + email + ", and your Readiness Score just went up.",
    "Next step whenever you're ready: your file goes to lenders anonymously, and they compete for you. Let's look at where you stand now.",
  ];
}

export function owenResumeSectionMessage(sectionTitle) {
  return 'Let\u2019s pick back up with \u201c' + sectionTitle + '\u201d.';
}

// ── Owen's take (insight cards) ─────────────────────────────────
export function getInsightFor(id, v, a) {
  if (id === 'credit') {
    if (v === '760+') return "Excellent credit — the very best rates on the market are open to you.";
    if (v === '700-759') return "Very good. You'll see competitive rates across nearly every program.";
    if (v === '660-699') return "Good spot — several strong programs fit this range.";
    if (v === '600-659' || v === '<600') return "Good to know. FHA and a few other programs are built for exactly this range — you have real options.";
    if (v === 'unsure') return "No problem — you can check it free without hurting your score. I'll assume mid-range for now.";
  }
  if (id === 'down' && a.purpose === 'purchase' && a.value) {
    const pct = (numFrom(v) / numFrom(a.value)) * 100;
    if (pct >= 20) return "20%+ down — that eliminates mortgage insurance entirely and unlocks the lowest rates. Great planning.";
    if (pct >= 10) return "Solid down payment. You're right in the sweet spot for conventional financing.";
    if (pct > 0) return "Totally workable — several programs are designed for smaller down payments, some as low as 3%.";
  }
  if (id === 'employment' && v === 'self') return "Self-employed? Nice — there are programs that qualify you on bank statements instead of tax returns.";
  if (id === 'military' && v === 'yes') return "Thank you for your service. You likely qualify for a VA loan — 0% down and no mortgage insurance.";
  if (id === 'state' && v === 'other') return "We're starting with California and expanding fast — I can still match you to programs, minus the location-specific ones for now.";
  if (id === 'county') {
    const r = ruralAnswer(a);
    if (r === 'yes') return "Good news — parts of that county are USDA-eligible, which could mean 0% down. I'll factor it in.";
    if (r === 'unsure') return "Some areas of that county may be USDA-eligible — I'll check the exact address later.";
  }
  return null;
}

// ── Canned side-answer fallbacks (used when the LLM is offline) ─
export function getCannedAnswer(q) {
  const t = q.toLowerCase();
  if (t.includes('pmi') || t.includes('mortgage insurance')) return "PMI is private mortgage insurance — an extra monthly charge when you put less than 20% down on a conventional loan. It protects the lender, not you, and it drops off automatically once you reach 20% equity.";
  if (t.includes('dti') || t.includes('debt-to-income') || t.includes('debt to income')) return "DTI is your debt-to-income ratio — your monthly debt payments divided by your monthly gross income. Most programs like it under 43%, and lower is always stronger.";
  if (t.includes('escrow')) return "Escrow is a holding account your lender manages — a slice of each monthly payment goes there to cover property taxes and homeowners insurance when they come due, so you don't get hit with big bills.";
  if (t.includes('apr')) return "APR is the annual percentage rate — your interest rate plus most fees, expressed as one number. It's the best apples-to-apples way to compare loan offers.";
  if (t.includes('point')) return "Points are optional upfront fees that buy down your interest rate — one point costs 1% of the loan amount. They can make sense if you'll keep the loan a long time.";
  if (t.includes('fha')) return "FHA loans are government-insured mortgages with flexible credit requirements and down payments as low as 3.5% — a great fit for many first-time buyers.";
  if (t.includes('va ') || t === 'va' || t.includes('veteran')) return "VA loans are for service members, veterans, and qualifying spouses — 0% down, no mortgage insurance, and competitive rates. One of the best benefits out there.";
  if (t.includes('usda')) return "USDA loans offer 0% down for homes in eligible rural and suburban areas — and more places qualify than most people expect.";
  if (t.includes('closing cost')) return "Closing costs are the one-time fees to finalize your loan — typically 2–5% of the loan amount, covering things like appraisal, title, and origination. On LoanKnow, lenders competing for you tends to push these down.";
  if (t.includes('credit')) return "Your credit score is the single biggest driver of your rate. Nothing we do here touches your credit — and checking your own score is always free and harmless.";
  if (t.includes('rate')) return "Rates move daily and depend mostly on your credit, down payment, and loan type. Once your file is ready, lenders compete on rate — that's where you win.";
  return "Good question. The short version: your options are driven mostly by credit, down payment, and debt-to-income — and everything you're doing here strengthens your position. Nothing is shared with anyone until you say so. Ask me about any term you run into.";
}

/** System prompt for the live LLM side-answer lane (mirrored in the edge function). */
export const OWEN_SIDE_ANSWER_SYSTEM_PROMPT =
  "You are Owen, LoanKnow's friendly AI mortgage advisor, chatting with a borrower mid-way through a readiness assessment. Answer their side question in 1-3 short, plain-English sentences. Warm, no jargon, no markdown, no lists. Educational only — never promise approval or quote a guaranteed rate. If asked about privacy or their data, reassure them: you are an AI, you don't view or retain personal information, and nothing is shared with lenders until they choose. If asked something unrelated to home financing, answer briefly and kindly steer back.";

// ── Compliance / legal copy ─────────────────────────────────────
export const COMPLIANCE_FOOTER_HTML = {
  nmlsUrl: 'https://www.nmlsconsumeraccess.org',
  text: '© 2026 LoanKnow, Inc. NMLS #1192984 (NMLS Consumer Access). Equal Housing Opportunity. LoanKnow is a technology platform, not a lender, and does not make credit decisions, originate loans, or issue loan commitments. Your Readiness Score is an educational estimate — not a credit score, loan approval, pre-qualification, or offer of credit. Rates shown are samples for illustration only; actual rates, fees, and terms are set by participating lenders and subject to their approval. This is not a commitment to lend. Nothing is shared with lenders until you choose to submit. We never sell your personal information. Owen is an AI assistant and does not provide financial, legal, or tax advice. Licensed availability varies by state; currently serving California.',
};

export const EMAIL_PROMISE = {
  title: 'Our email promise',
  body: "We do not market. We do not sell your information. We will never email you anything that didn't come from your direction — sign-in codes and the reports you ask for. That's it.",
};
